// src/lib/sheetLookup.js
// ───────────────────────────────────────────────────────────────────────────
// 🔎 Resolución ÚNICA de un escaneo → plancha / pieza (PLAN-TERMINAL-OPERADOR W2 · B1c)
//
// "Reusar, no duplicar": /scan, FileFinder y ahora /terminal repiten el mismo
// camino: parseScanInput(raw) → si trae IDs, lookup directo por documento; si es
// texto, UNA collectionGroup('sheets') por `hriSearch` (mata el N+1, §9.3 Pilar 3)
// y, si 0 planchas, fallback `array-contains` por `pieceCodes` (B1b). Aquí vive ese
// camino, una sola vez, sobre un `db` inyectado.
//
// Las operaciones de Firestore se inyectan (DI) para que `resolveScan` sea
// unit-testeable con un `db` falso, sin montar Firebase ni `jest.mock`. Los
// consumidores reales la llaman con dos args y usan las ops reales por defecto.
// ───────────────────────────────────────────────────────────────────────────
import {
  doc, getDoc, collection, collectionGroup, query, where, getDocs, limit,
} from 'firebase/firestore';
import { repsOf, printedFront, printedBack, cutOf, isTwoSided, printedDone } from './production';
import { parseScanInput } from './scanCode';
import { findPieceAcrossSheets } from './pieces.mjs';

// Tope de prefijo para queries por rango en Firestore (último char del rango).
// OJO: es el escape ASCII `\uf8ff`, NO el carácter literal (ver memoria
// "unicode-escapes-editing": el char invisible se cuela y rompe el match).
const PREFIX_END = '\uf8ff';

const defaultOps = { doc, getDoc, collection, collectionGroup, query, where, getDocs, limit };

/**
 * Estado de habilitación de los botones de marca de una plancha. PURO (sin
 * Firestore) — espejo exacto de la lógica inline de ScanningStationView.
 * @param {object} sheet
 * @return {{reps:number, twoSided:boolean, isPrinting:boolean, printedFront:number,
 *           printedBack:number, cut:number, canPrint:boolean, canPrintBack:boolean,
 *           canCut:boolean}}
 */
export function markAvailability(sheet) {
  const reps = repsOf(sheet);
  const twoSided = isTwoSided(sheet);
  const isPrinting = !!(sheet && sheet.isPrinting);
  const pf = printedFront(sheet);
  const pb = printedBack(sheet);
  const c = cutOf(sheet);
  const canPrint = isPrinting && pf < reps;
  const canPrintBack = twoSided && pb < reps;
  // El corte no puede exceder las reps ni, si la plancha imprime, lo ya impreso-completo.
  const canCut = c < reps && !(isPrinting && c >= printedDone(sheet));
  return { reps, twoSided, isPrinting, printedFront: pf, printedBack: pb, cut: c, canPrint, canPrintBack, canCut };
}

/**
 * Carga una plancha + su job (lookup directo por IDs).
 * @return {Promise<{sheet:object, jobData:object}|null>} null si la plancha no existe.
 */
export async function loadSheet(db, jobId, sheetId, ops = defaultOps) {
  const sheetSnap = await ops.getDoc(ops.doc(db, 'jobs', jobId, 'sheets', sheetId));
  if (!sheetSnap.exists()) return null;
  const jobSnap = await ops.getDoc(ops.doc(db, 'jobs', jobId));
  const jobData = jobSnap.exists() ? { id: jobSnap.id, ...jobSnap.data() } : { id: jobId };
  return { sheet: { ...sheetSnap.data(), id: sheetSnap.id, jobId }, jobData };
}

/** Agrega las planchas de un job y resuelve una pieza por código. */
async function resolvePieceInJob(db, jobId, code, ops) {
  const jobSnap = await ops.getDoc(ops.doc(db, 'jobs', jobId));
  if (!jobSnap.exists()) return { status: 'notfound', kind: 'piece', jobId, term: code };
  const sheetsSnap = await ops.getDocs(ops.collection(db, 'jobs', jobId, 'sheets'));
  const sheets = sheetsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const piece = findPieceAcrossSheets(sheets, code);
  if (!piece) return { status: 'notfound', kind: 'piece', jobId, term: code };
  return { status: 'piece', piece, jobId, jobName: jobSnap.data().jobName || jobId };
}

/**
 * Resuelve CUALQUIER entrada de escaneo (QR v1, QR legado, texto tecleado) a un
 * objetivo. Devuelve un objeto con `status`:
 *   - 'sheet'            → { sheet, jobData }                 (plancha lista para marcar)
 *   - 'sheetCandidates'  → { candidates:[sheet…] }            (texto matcheó varias planchas)
 *   - 'piece'            → { piece, jobId, jobName }          (panel read-only)
 *   - 'pieceCandidates'  → { candidates:[{jobId, piece}…] }   (la pieza vive en varios jobs)
 *   - 'artifact'         → etiqueta de archivo (va al Buscador de Archivos)
 *   - 'unknown'          → JSON de formato no reconocido
 *   - 'notfound'         → { term } no hay plancha ni pieza
 *   - 'empty'            → entrada vacía
 */
export async function resolveScan(db, raw, ops = defaultOps) {
  const parsed = parseScanInput(raw);
  if (!parsed) return { status: 'empty' };

  if (parsed.kind === 'sheet' && parsed.jobId && parsed.id) {
    const loaded = await loadSheet(db, parsed.jobId, parsed.id, ops);
    if (!loaded) return { status: 'notfound', kind: 'sheet', jobId: parsed.jobId };
    return { status: 'sheet', ...loaded };
  }
  if (parsed.kind === 'piece' && parsed.jobId && parsed.id) {
    return resolvePieceInJob(db, parsed.jobId, parsed.id, ops);
  }
  if (parsed.kind === 'artifact') return { status: 'artifact' };
  if (parsed.kind === 'unknown') return { status: 'unknown' };

  // Texto: hri tecleado o de etiqueta vieja → hriSearch normalizado (minúsculas, sin #).
  const term = parsed.hri;
  if (!term) return { status: 'empty' };

  const sheetSnap = await ops.getDocs(ops.query(
    ops.collectionGroup(db, 'sheets'),
    ops.where('hriSearch', '>=', term),
    ops.where('hriSearch', '<=', term + PREFIX_END),
    ops.limit(25),
  ));
  const found = sheetSnap.docs.map((d) => ({
    ...d.data(), id: d.id, jobId: d.ref.parent.parent.id,
  }));
  if (found.length === 1) {
    // Coincidencia única → abrir directo (menos taps en el terminal).
    const loaded = await loadSheet(db, found[0].jobId, found[0].id, ops);
    if (loaded) return { status: 'sheet', ...loaded };
  }
  if (found.length > 0) return { status: 'sheetCandidates', candidates: found, term };

  // Ninguna plancha → probar como CÓDIGO DE PIEZA exacto (array-contains, B1b).
  const pieceSnap = await ops.getDocs(ops.query(
    ops.collectionGroup(db, 'sheets'),
    ops.where('pieceCodes', 'array-contains', term),
    ops.limit(50),
  ));
  if (!pieceSnap.empty) {
    const byJob = new Map();
    pieceSnap.docs.forEach((d) => {
      const jid = d.ref.parent.parent.id;
      if (!byJob.has(jid)) byJob.set(jid, []);
      byJob.get(jid).push({ id: d.id, ...d.data() });
    });
    const results = [];
    for (const [jid, sheets] of byJob.entries()) {
      const piece = findPieceAcrossSheets(sheets, term);
      if (piece) results.push({ jobId: jid, piece });
    }
    if (results.length === 1) {
      return resolvePieceInJob(db, results[0].jobId, term, ops);
    }
    if (results.length > 1) return { status: 'pieceCandidates', candidates: results, term };
  }

  return { status: 'notfound', term };
}
